import { useField, UseFieldConfig, FieldRenderProps } from 'react-final-form'
import { FieldState } from 'final-form'

import { getRulesForPath } from 'validation'
import { Data } from 'interfaces/ApolloResponse.interface'
import useStore from 'stores/utils/hooks/useStore'
import { useError } from 'stores/utils/hooks/useError'

export type FinalFormFieldProps<T> = FieldRenderProps<T, HTMLElement> & {
  error?: string
}

/**
 * The `useField` hook from FinalForm is more or less unchanged.
 * The `validate` method of the passed in fieldConfig is
 * used to trigger the handlePathFormFieldChange.
 *
 * @param {string} fieldPath The path in the Traversal, e.g. `debtor.primary.expenses.rent`
 * @param {UseFieldConfig} fieldConfig see FinalForm documentation for `UseFieldConfig`
 * @param {boolean} patch Indicating wether to execute the PATCH request, defaults to `true`
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePatchedField<T = any>(
  fieldPath: string,
  fieldConfig: UseFieldConfig,
  // eslint-disable-next-line default-param-last
  patch = true,
  internalValidation?: (value: T) => boolean
): FinalFormFieldProps<T> {
  const {
    page: { config },
    traversal: { getFieldValueByPath, handlePathFormFieldChange }
  } = useStore()

  const field = useField(fieldPath, {
    ...fieldConfig,
    subscription: {
      value: true,
      error: true,
      submitError: true,
      dirtySinceLastSubmit: true,
      touched: true
    },
    validate: (value, allValues, meta) => {
      const globalRules = getRulesForPath(fieldPath)
      const globalValidationResults = globalRules.map((validationFunction) =>
        validationFunction(
          value,
          allValues as Data,
          config,
          meta as FieldState<unknown> | undefined
        )
      )

      const hasInternalValidationErrors =
        internalValidation && value ? !internalValidation(value) : false

      const hasGlobalValidationErrors =
        globalValidationResults.filter(Boolean).length !== 0

      /**
       * Only sync/patch the field when:
       * - no frontend validation errors
       * - the field was modified by the user
       * - the MobX store value differs from the current form value
       *
       * The last check is critical: handlePathFormFieldChange always
       * reassigns `this.data` with a new object reference (via toJS +
       * rebuild), which triggers the `initialValues` useMemo in
       * Routes.tsx to recompute, causing Final Form to re-initialise
       * and re-validate every field — creating an infinite loop when
       * the value hasn't actually changed.
       */
      if (
        !hasInternalValidationErrors &&
        !hasGlobalValidationErrors &&
        meta?.modified
      ) {
        const storeValue = getFieldValueByPath(fieldPath)

        if (storeValue !== value) {
          handlePathFormFieldChange(fieldPath, value, patch)
        }
      }

      const externalValidation = fieldConfig.validate
      const externalValidationResult = []

      if (externalValidation) {
        const tmp = externalValidation(value, allValues, meta)

        if (!Array.isArray(tmp)) {
          externalValidationResult.push(tmp)
        } else {
          externalValidationResult.push(...tmp)
        }
      }

      const errors = [...externalValidationResult, ...globalValidationResults]
      const errorsWithoutNullishValues = errors.filter(Boolean)

      if (errorsWithoutNullishValues.length === 0) {
        return undefined
      }

      return errorsWithoutNullishValues
    }
  })

  const { error } = useError(fieldPath)
  return {
    ...field,
    error
  }
}
